import { useMemo, useRef } from 'react';
import {
  Filter as CoreFilter,
  FilterOption,
} from '@ktag/styleguide/dist/components/filter/react/ExpandableFilter/expandableFilter.types';
import { AttributeId, Attributes, AttributeValue, Filter, JobResult } from 'jobs.types';
import { useTranslation } from 'react-i18next';
import {
  ARBEITSORT_ATTRIBUTE_ID,
  BEFRISTUNG_ATTRIBUTE_ID,
  HOMEOFFICE_ATTRIBUTE_ID,
  RUBRIK_ATTRIBUTE_ID,
  STELLART_ATTRIBUTE_ID,
  RESET_ATTRIBUTE_ID,
} from '../constants';
import updateSearchParameters, { ADDITIONALS_KEY } from './updateSearchParameters';

const getOnChangeFunction = (
  filterId: string,
  filter: Filter,
  setFilter: (filter: Filter) => void,
) => {
  return (value: string | string[]) => {
    updateSearchParameters('search', filterId, value, filter, setFilter);
  };
};

const SORTABLE_ATTRIBUTES = [ARBEITSORT_ATTRIBUTE_ID, RUBRIK_ATTRIBUTE_ID];

const sortOptions = (options: FilterOption[], id: AttributeId) => {
  if (SORTABLE_ATTRIBUTES.includes(id)) {
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }
  return options;
};

const useGetFilters = (
  attributes: Attributes,
  jobs: JobResult[],
  filter: Filter,
  setFilter: (filter: Filter) => void,
) => {
  const timeoutIdMin = useRef(null);
  const timeoutIdMax = useRef(null);

  const { t } = useTranslation('jobs');

  const attributesWithCount = useMemo(() => {
    if (!attributes || !jobs) {
      return null;
    }
    const count = {
      [ARBEITSORT_ATTRIBUTE_ID]: {},
      [RUBRIK_ATTRIBUTE_ID]: {},
      [STELLART_ATTRIBUTE_ID]: {},
      [BEFRISTUNG_ATTRIBUTE_ID]: {},
      [HOMEOFFICE_ATTRIBUTE_ID]: {},
    };
    Object.entries(attributes)?.forEach(([key, value]) => {
      count[key] = {};
      value.values.forEach((v) => {
        count[key][v.id] = jobs.filter((job) =>
          job.attributes[key]?.includes(v.name),
        )?.length;
      });
    });

    count[HOMEOFFICE_ATTRIBUTE_ID]['1'] = jobs.filter((job) =>
      job.attributes[HOMEOFFICE_ATTRIBUTE_ID]?.includes('1'),
    )?.length;

    return count;
  }, [attributes, jobs]);

  const filters: CoreFilter[] = useMemo(() => {
    if (!attributes || !jobs) return null;

    const sortedAttributes = [
      { id: ARBEITSORT_ATTRIBUTE_ID, attribute: attributes[ARBEITSORT_ATTRIBUTE_ID] },
      { id: RUBRIK_ATTRIBUTE_ID, attribute: attributes[RUBRIK_ATTRIBUTE_ID] },
      { id: STELLART_ATTRIBUTE_ID, attribute: attributes[STELLART_ATTRIBUTE_ID] },
    ];

    const baseFilters: Array<CoreFilter> = sortedAttributes.map(({ id, attribute }) => {
      const {
        id: attId,
        name,
        values,
      } = attribute as {
        id: AttributeId;
        name: string;
        values: AttributeValue[];
      };

      return {
        label: name,
        name: id,
        type: 'multiselect',
        onChange: getOnChangeFunction(id, filter, setFilter),
        initialValue: filter.search[id] || [],
        options: sortOptions(
          values
            .filter((value) => !(value.id === RESET_ATTRIBUTE_ID))
            .map((value) => ({
              label: value.name,
              value: value.id,
              count: attributesWithCount[attId][value.id],
            })),
          attId,
        ),
      };
    });

    baseFilters.splice(1, 0, {
      label: t('filters_pensum'),
      name: 'pensum',
      type: 'sliderrange',
      setRangeMin: (value: number) => {
        if (timeoutIdMin.current) {
          clearTimeout(timeoutIdMin.current);
        }
        timeoutIdMin.current = setTimeout(() => {
          updateSearchParameters(
            'search',
            'sza_pensum.min',
            [`${value}`],
            filter,
            setFilter,
          );
        }, 300);
      },
      setRangeMax: (value: number) => {
        if (timeoutIdMax.current) {
          clearTimeout(timeoutIdMax.current);
        }
        timeoutIdMax.current = setTimeout(() => {
          updateSearchParameters(
            'search',
            'sza_pensum.max',
            [`${value}`],
            filter,
            setFilter,
          );
        }, 300);
      },
      initialValues: {
        min: 0,
        max: 100,
      },
      value: {
        min: parseInt(filter.search['sza_pensum.min']?.[0], 10),
        max: parseInt(filter.search['sza_pensum.max']?.[0], 10),
      },
      unit: '%',
      rangeStep: 5,
    });

    const additionalOptions = attributes['70']?.values
      .filter((value) => !(value.id === RESET_ATTRIBUTE_ID))
      .map((value) => ({
        label: value.name,
        value: value.id,
        count: attributesWithCount[BEFRISTUNG_ATTRIBUTE_ID][value.id],
      }));

    additionalOptions?.unshift({
      label: t('filters_additional_remote'),
      value: 'remote',
      count: attributesWithCount[HOMEOFFICE_ATTRIBUTE_ID]['1'],
    });

    if (additionalOptions) {
      baseFilters.push({
        label: t('filters_additional'),
        name: 'additional',
        type: 'multiselect',
        onChange: (value: string | string[]) =>
          updateSearchParameters('search', ADDITIONALS_KEY, value, filter, setFilter),
        options: additionalOptions,
      });
    }

    return baseFilters;
  }, [attributes, jobs]);

  return filters;
};

export default useGetFilters;
